import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProducts, Product } from '../../../hooks/useProducts';
import { useCart } from '../../../contexts/CartContext';

function ProductCard({ product, onAddToCart, added }: {
  product: Product;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  added: boolean;
}) {
  const allImages = [product.image_url, ...(product.galeri_gorseller || [])].filter(Boolean);
  const [imgIndex, setImgIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const handleMouseEnter = () => {
    setHovered(true);
    if (allImages.length <= 1) return;
    let idx = 1;
    intervalRef.current = setInterval(() => {
      setImgIndex(idx % allImages.length);
      idx++;
    }, 800);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setImgIndex(0);
  };

  return (
    <Link
      to={`/urun/${product.slug}`}
      className="group bg-cream-light rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative bg-cream overflow-hidden" style={{ aspectRatio: '2/3' }}>
        {allImages.length > 0 && (
          <img
            src={allImages[imgIndex]}
            alt={product.name}
            className="w-full h-full object-cover object-center transition-opacity duration-300"
          />
        )}
        {product.badge && (
          <span className="absolute top-3 left-3 px-2.5 py-1 bg-gold text-white text-xs font-semibold rounded-lg">
            {product.badge}
          </span>
        )}
        {product.original_price && (
          <span className="absolute top-3 right-3 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-lg">
            %{Math.round((1 - product.price / product.original_price) * 100)}
          </span>
        )}
        {allImages.length > 1 && hovered && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
            {allImages.slice(0, 5).map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${i === imgIndex ? 'bg-white scale-125' : 'bg-white/50'}`} />
            ))}
          </div>
        )}
        {product.stock > 0 && (
          <button
            onClick={(e) => onAddToCart(e, product)}
            className={`absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${added ? 'bg-green-500 scale-110' : 'bg-coffee hover:bg-gold'} text-white opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0`}
            aria-label="Sepete ekle"
          >
            <i className={`text-base ${added ? 'ri-check-line' : 'ri-shopping-bag-line'}`} />
          </button>
        )}
        {product.stock <= 0 && (
          <span className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/60 text-white text-[10px] font-semibold rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Tükendi
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-gold font-medium mb-1">{product.category_name}</p>
        <h3 className="font-serif text-sm font-semibold text-coffee leading-snug mb-3 group-hover:text-gold transition-colors line-clamp-2">
          {product.name}
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-coffee">
              {product.price.toLocaleString('tr-TR')}₺
            </span>
            {product.original_price && (
              <span className="text-sm text-coffee-light line-through">
                {product.original_price.toLocaleString('tr-TR')}₺
              </span>
            )}
          </div>
          {product.weight && (
            <span className="text-xs text-coffee-light">{product.weight}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function ProductsSection() {
  const { addItem } = useCart();
  const [addedId, setAddedId] = useState<string | null>(null);
  const { products, loading } = useProducts();
  const featured = products.slice(0, 8);

  const handleAddToCart = (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    if (product.stock <= 0) return;
    addItem({
      id: product.id,
      product_id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      slug: product.slug,
      weight: product.weight,
      stock: product.stock,
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1800);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-gold font-medium text-sm tracking-widest uppercase mb-3">Öne Çıkanlar</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-coffee">Seçilmiş Kahveler</h2>
          </div>
          <Link to="/urunler" className="flex items-center gap-2 text-sm font-medium text-coffee-medium hover:text-gold transition-colors whitespace-nowrap">
            Tümünü Gör
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="bg-cream-light rounded-2xl overflow-hidden animate-pulse">
                <div className="bg-cream" style={{ aspectRatio: '2/3' }} />
                <div className="p-4 space-y-2">
                  <div className="h-3 bg-cream rounded w-2/3" />
                  <div className="h-4 bg-cream rounded" />
                  <div className="h-4 bg-cream rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {featured.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
                added={addedId === product.id}
              />
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link to="/urunler" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-coffee text-coffee font-semibold rounded-xl hover:bg-coffee hover:text-white transition-all duration-300 whitespace-nowrap">
            Tüm Koleksiyonu Gör
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
